/**
 * @fileOverview State machine for the onboarding walkthrough.
 *
 * Module-level singleton, mirroring useDynamicDialog: the walkthrough has to survive route changes
 * and is driven from two places (auto-start after the first login, "start tour" in the help menu), so
 * its state cannot live in a component. LewOnboardingTour renders it; this file owns everything else.
 *
 * Three screens, tracked by {@link view}:
 *
 *   "welcome" — the arrival screen, shown once after registration
 *   "steps"   — the walkthrough, one video per step, grouped into chapters
 *   "outro"   — where to find it again
 */

import { computed, ref } from "vue"
import { useAuthStore } from "@/store/authentication"
import { isNavEntryEnabled } from "@/services/navigation"
import {
  ONBOARDING_CHAPTERS,
  ONBOARDING_STEPS,
  UPSELLABLE_FEATURE_SETS,
  OnboardingChapter,
  OnboardingStep,
} from "@/services/onboardingTour"
import { hasPermissionsOfAtLeastOne } from "@/services/permissionService"

export type TourView = "welcome" | "steps" | "outro"

const isActive = ref(false)
const view = ref<TourView>("welcome")
const stepIndex = ref(0)

/** Guards the auto-start so it can only ever fire once per page load. */
const autoStartAttempted = ref(false)

export function useOnboardingTour() {
  /**
   * The steps this tenant and user can actually see. A feature the tenant has not bought is dropped
   * entirely rather than shown as a locked teaser — the outro carries one generic upsell line
   * instead. `navEntry` reuses the drawer's own permission gate; see `isNavEntryEnabled`.
   */
  const steps = computed<OnboardingStep[]>(() => {
    const { entitlements } = useAuthStore()
    return ONBOARDING_STEPS.filter(step => {
      const entitled = !step.featureSets || step.featureSets.some(fs => entitlements.includes(fs))
      const permitted = !step.permissions || hasPermissionsOfAtLeastOne(step.permissions).value
      const navigable = !step.navEntry || isNavEntryEnabled(step.navEntry)
      return entitled && permitted && navigable
    })
  })

  const step = computed<OnboardingStep | null>(() => steps.value[stepIndex.value] ?? null)
  const isFirst = computed(() => stepIndex.value === 0)
  const isLast = computed(() => stepIndex.value >= steps.value.length - 1)

  const chapter = computed<OnboardingChapter | null>(
    () => ONBOARDING_CHAPTERS.find(c => c.key === step.value?.chapter) ?? null
  )

  /** Chapters with at least one surviving step, in catalog order — the progress read-out's units. */
  const visibleChapters = computed<OnboardingChapter[]>(() =>
    ONBOARDING_CHAPTERS.filter(c => steps.value.some(s => s.chapter === c.key))
  )

  /**
   * Sellable feature-sets the tenant does not have. Empty while entitlements are unknown: a real tenant
   * always has at least CORE, so an empty entitlement list means "no information", and we must never
   * infer "you don't own X" from missing data. An unreleased set is left out: it is in no plan and no
   * add-on list, so a hint counting it would point at something the plans page cannot sell.
   */
  const missingFeatureSets = computed<string[]>(() => {
    const { entitlements, unreleasedFeatureSets } = useAuthStore()
    if (!entitlements.length) return []
    return UPSELLABLE_FEATURE_SETS.filter(fs => !entitlements.includes(fs) && !unreleasedFeatureSets.includes(fs))
  })

  /** Closes without recording it as seen — for accidental exits like a route change. */
  const deactivate = () => {
    isActive.value = false
  }

  /** Closes and remembers it, so it does not come back. */
  const complete = () => {
    deactivate()
    useAuthStore().completeOnboarding()
  }

  /** Opens on the welcome screen. */
  const start = () => {
    view.value = "welcome"
    stepIndex.value = 0
    isActive.value = true
  }

  /** Manual entry point (help menu). Same as start — someone asking for it wants it from the top. */
  const restart = start

  /** Leaves the welcome screen for the walkthrough. Straight to the outro if nothing survived gating. */
  const beginSteps = () => {
    stepIndex.value = 0
    view.value = steps.value.length ? "steps" : "outro"
  }

  const next = () => {
    if (isLast.value) view.value = "outro"
    else stepIndex.value += 1
  }

  const previous = () => {
    if (view.value === "outro") {
      // Back out of the outro onto the last step, not out of the walkthrough.
      view.value = "steps"
      stepIndex.value = Math.max(0, steps.value.length - 1)
    } else if (view.value === "steps" && isFirst.value) view.value = "welcome"
    else stepIndex.value -= 1
  }

  return {
    isActive,
    view,
    steps,
    step,
    stepIndex,
    chapter,
    visibleChapters,
    isFirst,
    isLast,
    missingFeatureSets,
    autoStartAttempted,
    start,
    restart,
    beginSteps,
    next,
    previous,
    skip: complete,
    finish: complete,
    deactivate,
  }
}
